import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

import useCurrentEnvironment from "@/hooks/use-environment";
import useFeatureFlags from "@/hooks/useFeatureFlag";
import {
  loadWizardSessions,
  parseWizardSessions,
  saveWizardSessions,
  wizardStorageKey,
} from "./storage";
import {
  AnyWizardDefinition,
  DataPatch,
  WizardSession,
  WizardSessionsState,
} from "./types";
import {
  findMatchingSessions,
  initialWizardSessionsState,
  wizardSessionsReducer,
} from "./wizardSessionsReducer";

type WizardsContextValue = {
  /** False while wizards are flagged off; every action is then a no-op. */
  enabled: boolean;
  definitions: Record<string, AnyWizardDefinition>;
  state: WizardSessionsState;
  /**
   * Opens a wizard, resuming the in-progress session that `params` would
   * start (see `getSessionKey`) if there is one.
   */
  openWizard: (wizardType: string, params?: unknown) => void;
  /** The in-progress session `openWizard(wizardType, params)` would resume. */
  findInProgressSession: (
    wizardType: string,
    params?: unknown,
  ) => WizardSession | undefined;
  /** Starts the session over with the params it was opened with. */
  restartWizard: (sessionId: string) => void;
  expandWizard: (sessionId: string) => void;
  minimizeWizard: () => void;
  discardWizard: (sessionId: string) => void;
  updateWizardData: (sessionId: string, patch: DataPatch) => void;
  goToWizardStep: (
    sessionId: string,
    stepId: string,
    completedStepId?: string,
  ) => void;
  completeWizard: (sessionId: string, completedStepId: string) => void;
  /** Saves the steps that apply to the session, for the dock's progress. */
  recordApplicableSteps: (sessionId: string, stepIds: string[]) => void;
};

const WizardsContext = createContext<WizardsContextValue | null>(null);

const noop = () => {};

const disabledWizards: WizardsContextValue = {
  enabled: false,
  definitions: {},
  state: initialWizardSessionsState,
  openWizard: noop,
  findInProgressSession: () => undefined,
  restartWizard: noop,
  expandWizard: noop,
  minimizeWizard: noop,
  discardWizard: noop,
  updateWizardData: noop,
  goToWizardStep: noop,
  completeWizard: noop,
  recordApplicableSteps: noop,
};

export function useWizards(): WizardsContextValue {
  const context = useContext(WizardsContext);
  if (!context) {
    throw new Error("useWizards must be used within a WizardProvider");
  }
  return context;
}

export type MinimizedWizard = {
  definition: AnyWizardDefinition;
  session: WizardSession;
};

/** Sessions shown in the dock, oldest first. Empty outside a provider. */
export function useMinimizedWizards(): MinimizedWizard[] {
  const context = useContext(WizardsContext) ?? disabledWizards;
  const { definitions, state } = context;
  return useMemo(
    () =>
      Object.values(state.sessions)
        .filter((session) => session.id !== state.expandedId)
        .flatMap((session) => {
          const definition = definitions[session.type];
          return definition ? [{ definition, session }] : [];
        })
        .sort((a, b) => a.session.startedAt - b.session.startedAt),
    [definitions, state.sessions, state.expandedId],
  );
}

function newSessionId(wizardType: string): string {
  return `${wizardType}:${crypto.randomUUID()}`;
}

type WizardProviderProps = PropsWithChildren<{
  definitions: AnyWizardDefinition[];
  /** The signed-in user, or null when signed out. Sessions are per user. */
  userID: string | null;
  storage?: Storage;
}>;

export function WizardProvider({
  definitions: definitionList,
  userID,
  storage = window.localStorage,
  children,
}: WizardProviderProps) {
  const definitions = useMemo(
    () =>
      Object.fromEntries(
        definitionList.map((definition) => [definition.type, definition]),
      ),
    [definitionList],
  );
  const [state, dispatch] = useReducer(
    wizardSessionsReducer,
    initialWizardSessionsState,
  );
  const envID = useCurrentEnvironment()?.ID ?? null;
  const enabled = useFeatureFlags()("homepageAndOnboarding") && !!userID;

  useEffect(() => {
    if (!userID) {
      dispatch({ type: "reset" });
      return;
    }
    if (!enabled || !envID) {
      return;
    }
    dispatch({
      type: "hydrate",
      userID,
      envID,
      snapshot: loadWizardSessions(storage, { userID, envID }, definitions),
    });
  }, [enabled, userID, envID, storage, definitions]);

  useEffect(() => {
    if (!enabled || !state.userID || !state.envID) {
      return;
    }
    saveWizardSessions(
      storage,
      { userID: state.userID, envID: state.envID },
      { sessions: state.sessions, discarded: state.discarded },
      definitions,
    );
  }, [
    enabled,
    state.userID,
    state.envID,
    state.sessions,
    state.discarded,
    storage,
    definitions,
  ]);

  useEffect(() => {
    if (!enabled || !state.userID || !state.envID) {
      return;
    }
    const scope = { userID: state.userID, envID: state.envID };
    const key = wizardStorageKey(scope);
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== storage || event.key !== key) {
        return;
      }
      dispatch({
        type: "merge",
        ...scope,
        snapshot: parseWizardSessions(event.newValue, definitions, Date.now()),
      });
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [enabled, state.userID, state.envID, storage, definitions]);

  // Until the sessions for the current user and environment load, show none
  // rather than the previous environment's.
  const visibleState =
    state.userID === userID && state.envID === envID
      ? state
      : initialWizardSessionsState;
  const sessions = visibleState.sessions;

  const openWizard = useCallback(
    (wizardType: string, params?: unknown) => {
      const definition = definitions[wizardType];
      if (!definition) {
        console.warn(`Unknown wizard type "${wizardType}"`);
        return;
      }
      dispatch({
        type: "open",
        sessionId: newSessionId(wizardType),
        wizardType,
        firstStepId: definition.steps[0].id,
        initialData: definition.createInitialData(params),
        params,
        getSessionKey: definition.getSessionKey,
        now: Date.now(),
      });
    },
    [definitions],
  );

  const restartWizard = useCallback(
    (sessionId: string) => {
      const session = sessions[sessionId];
      const definition = session && definitions[session.type];
      if (!definition) {
        return;
      }
      dispatch({
        type: "restart",
        replacedSessionId: sessionId,
        sessionId: newSessionId(definition.type),
        wizardType: definition.type,
        firstStepId: definition.steps[0].id,
        initialData: definition.createInitialData(session.params),
        params: session.params,
        getSessionKey: definition.getSessionKey,
        now: Date.now(),
      });
    },
    [definitions, sessions],
  );

  const findInProgressSession = useCallback(
    (wizardType: string, params?: unknown) => {
      const definition = definitions[wizardType];
      if (!definition) {
        return undefined;
      }
      return findMatchingSessions(
        sessions,
        wizardType,
        definition.createInitialData(params),
        definition.getSessionKey,
      ).find((session) => session.completedAt === null);
    },
    [definitions, sessions],
  );

  const value = useMemo<WizardsContextValue>(
    () => ({
      enabled: true,
      definitions,
      state: visibleState,
      openWizard,
      findInProgressSession,
      restartWizard,
      expandWizard: (sessionId) => dispatch({ type: "expand", sessionId }),
      minimizeWizard: () => dispatch({ type: "minimize" }),
      discardWizard: (sessionId) =>
        dispatch({ type: "discard", sessionId, now: Date.now() }),
      updateWizardData: (sessionId, patch) =>
        dispatch({ type: "updateData", sessionId, patch, now: Date.now() }),
      goToWizardStep: (sessionId, stepId, completedStepId) =>
        dispatch({
          type: "goToStep",
          sessionId,
          stepId,
          completedStepId,
          now: Date.now(),
        }),
      completeWizard: (sessionId, completedStepId) =>
        dispatch({
          type: "complete",
          sessionId,
          completedStepId,
          now: Date.now(),
        }),
      recordApplicableSteps: (sessionId, stepIds) =>
        dispatch({ type: "recordApplicableSteps", sessionId, stepIds }),
    }),
    [
      definitions,
      visibleState,
      openWizard,
      findInProgressSession,
      restartWizard,
    ],
  );

  return (
    <WizardsContext.Provider value={enabled ? value : disabledWizards}>
      {children}
    </WizardsContext.Provider>
  );
}
